import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { rangeParams, RANGE_OPTIONS } from "@/lib/dateRanges";
import { useNodeExport } from "@/hooks/useNodeExport";
import TabReportPrint from "@/components/shared/TabReportPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  Legend,
} from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { TrendingUp, Loader2, Sparkles, CalendarRange, Target } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];
const QUOTA_KEY = "salestap-forecast-quota";
const CAT_COLORS = {
  Commit: "#16a34a",
  "Best case": "#2563eb",
  Pipeline: "#f59e0b",
  Omitted: "#94a3b8",
  Uncategorized: "#cbd5e1",
};

function Kpi({ label, value, sub, testId }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-semibold text-foreground mt-1" data-testid={testId}>{value}</p>
        {sub ? <p className="text-xs text-muted-foreground mt-1">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

export default function Forecast({ range, onRangeChange, onRegisterExport, theme }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [quota, setQuota] = useState(() => sessionStorage.getItem(QUOTA_KEY) || "");
  const printRef = useRef(null);
  const rangeLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || "All Time";

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!data,
    "salestap-forecast"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("forecast", { exportPdf, printFn: printNode });
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { start, end } = rangeParams(range);
      const [f, rep] = await Promise.all([
        api.getForecast(start, end),
        api.getForecastReport().catch(() => ({ report_md: null })),
      ]);
      setData(f);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load forecast:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const onQuotaChange = (e) => {
    const v = e.target.value.replace(/[^0-9.]/g, "");
    setQuota(v);
    if (v) sessionStorage.setItem(QUOTA_KEY, v);
    else sessionStorage.removeItem(QUOTA_KEY);
  };

  const generate = async () => {
    setReportLoading(true);
    try {
      const { start, end } = rangeParams(range);
      await api.generateForecastReport(start, end);
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.forecastReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Forecast analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const open = data?.open_pipeline_value || 0;
  const weighted = data?.weighted_forecast || 0;
  const byMonth = data?.by_month || [];
  const byQuarter = data?.by_quarter || [];
  const categories = data?.categories || [];
  const closing = data?.closing_in_period || { count: 0, value: 0, weighted: 0, deals: [] };

  const quotaNum = parseFloat(quota) || 0;
  const gapToGoal = quotaNum > 0 ? quotaNum - weighted : null; // vs weighted forecast
  const coverage = quotaNum > 0 ? open / quotaNum : null; // pipeline coverage ratio

  const axisColor = theme === "dark" ? "#94a3b8" : "#64748b";
  const gridColor = theme === "dark" ? "#1e293b" : "#e2e8f0";

  const subtitle = `${fmtNumber(data?.open_deals || 0)} open deals · ${fmtCurrency(open)} pipeline · ${fmtCurrency(weighted)} weighted · ${rangeLabel}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Forecast</h2>
            <p className="text-xs text-muted-foreground">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Target className="w-4 h-4 text-muted-foreground" />
            <Input
              data-testid={TID.forecastQuotaInput}
              value={quota}
              onChange={onQuotaChange}
              inputMode="decimal"
              placeholder="Target / quota ($)"
              className="w-[150px] h-9"
            />
          </div>
          <CalendarRange className="w-4 h-4 text-muted-foreground" />
          <Select value={range} onValueChange={onRangeChange}>
            <SelectTrigger className="w-[150px]" data-testid={TID.forecastRangeSelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid={TID.genForecastReportBtn} onClick={generate} disabled={reportLoading} variant="outline" size="sm">
            {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {report ? "Regenerate" : "AI Analysis"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Open Pipeline" value={fmtCurrency(open)} sub={`${fmtNumber(data?.open_deals || 0)} deals`} />
        <Kpi label="Weighted Forecast" value={fmtCurrency(weighted)} sub="probability-adjusted" />
        <Kpi
          label="Gap to Goal"
          value={gapToGoal === null ? "—" : fmtCurrency(gapToGoal)}
          sub={quotaNum > 0 ? `vs ${fmtCurrency(quotaNum)} target` : "set a target"}
        />
        <Kpi
          label="Pipeline Coverage"
          value={coverage === null ? "—" : `${coverage.toFixed(1)}x`}
          sub={quotaNum > 0 ? "open ÷ target" : "set a target"}
        />
      </div>

      {/* Forecast by month */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Forecast by Month <span className="text-xs font-normal text-muted-foreground ml-1">· next 6 months (by close date)</span></CardTitle>
        </CardHeader>
        <CardContent>
          {byMonth.some((m) => m.value > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byMonth} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: axisColor }} axisLine={{ stroke: gridColor }} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="value" name="Pipeline" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="weighted" name="Weighted" fill="#A5122F" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground py-10 text-center">No open deals with future close dates in the next 6 months.</p>
          )}
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* By quarter */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Forecast by Quarter</CardTitle>
          </CardHeader>
          <CardContent>
            {byQuarter.some((q) => q.value > 0) ? (
              <div className="space-y-2">
                {byQuarter.map((q) => (
                  <div key={q.key} className="flex items-center justify-between text-sm border-b border-border/60 pb-1.5">
                    <span className="text-foreground/80">{q.label}</span>
                    <span className="flex items-center gap-3">
                      <span className="font-medium">{fmtCurrency(q.value)}</span>
                      <span className="text-xs text-muted-foreground">wtd {fmtCurrency(q.weighted)}</span>
                      <span className="text-xs text-muted-foreground">{fmtNumber(q.count)} deals</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">No quarterly forecast data.</p>
            )}
          </CardContent>
        </Card>

        {/* Category confidence */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Forecast Category</CardTitle>
          </CardHeader>
          <CardContent>
            {categories.length ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={categories} layout="vertical" margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={gridColor} horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: axisColor }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="category" tick={{ fontSize: 12, fill: axisColor }} axisLine={false} tickLine={false} width={90} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {categories.map((c) => (
                      <Cell key={c.category} fill={CAT_COLORS[c.category] || "#cbd5e1"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-6 text-center">No forecast-category data (HubSpot only).</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Deals closing in period */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Deals Closing in Period <span className="text-xs font-normal text-muted-foreground ml-1">· {rangeLabel} · {fmtNumber(closing.count)} deals · {fmtCurrency(closing.value)}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {closing.deals?.length ? (
            <div className="space-y-1 max-h-[280px] overflow-y-auto">
              {closing.deals.map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm border-b border-border/60 py-1">
                  <span className="truncate mr-2 text-foreground/80">{d.name} <span className="text-xs text-muted-foreground">· {d.stage} · {d.owner}</span></span>
                  <span className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">{d.probability}%</span>
                    <span className="font-medium">{fmtCurrency(d.amount)}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-6 text-center">No deals expected to close in the selected period.</p>
          )}
        </CardContent>
      </Card>

      {report && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> AI Forecast Review
            </CardTitle>
          </CardHeader>
          <CardContent>
            <article data-testid={TID.forecastReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
            </article>
          </CardContent>
        </Card>
      )}

      <TabReportPrint ref={printRef} title="Forecast" subtitle={subtitle} reportMd={report}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10.5 }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #0f172a" }}>
              <th style={{ textAlign: "left", padding: 4 }}>Month</th>
              <th style={{ textAlign: "right", padding: 4 }}>Pipeline</th>
              <th style={{ textAlign: "right", padding: 4 }}>Weighted</th>
              <th style={{ textAlign: "right", padding: 4 }}>Deals</th>
            </tr>
          </thead>
          <tbody>
            {byMonth.map((m) => (
              <tr key={m.key} style={{ borderBottom: "1px solid #e2e8f0" }}>
                <td style={{ padding: 4 }}>{m.label}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(m.value)}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{fmtCurrency(m.weighted)}</td>
                <td style={{ textAlign: "right", padding: 4 }}>{m.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TabReportPrint>
    </div>
  );
}
